
































































































































































































import { Component, Vue, Prop, Ref, Watch } from 'vue-property-decorator';
import VueSlider from 'vue-slider-component';
import { Storage } from 'aws-amplify';
import { State } from 'vuex-class';

type Num = number;
interface PhotoLite {
  id?: string;
  key: string;
  time: Date | string | number;
}
interface Det {
  conf: number;
  area_pct: number;
}
interface Row {
  id: string;
  key: string;
  t: number;
  goutte: number;
  area_pct: number;
  count: number;
  detections?: Det[]; // <--- NEW
  annotatedKey?: string;
}

function stripPublic(k: string | null | undefined): string {
  const s = String(k || '').replace(/^\/+/, '');
  return s.replace(/^(?:public\/)+/i, '');
}

function normalizePublicKey(input: string): string {
  const raw = String(input || '')
    .split('?')[0]
    .replace(/^\/+/, '');

  // URL complète -> extraire après /public|protected|private/
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      const m = u.pathname.match(/\/(public|protected|private)\/(.+)$/);
      if (m) return m[2]; // => clé relative SANS "public/"
      return u.pathname.replace(/^\/+/, '');
    } catch {
      /* ignore */
    }
  }

  // Si commence par public/, on le retire pour Amplify.get(level:'public')
  return raw.startsWith('public/') ? raw.slice('public/'.length) : raw;
}

async function getSignedUrlPublic(key: string) {
  const rel = normalizePublicKey(key); // ⬅️ IMPORTANT
  const url = await Storage.get(rel, {
    level: 'public',
    expires: 900,
    cacheControl: 'max-age=60',
  });
  return String(url);
}

function keyForLambda(raw: string): string {
  const s = String(raw || '').split('?')[0];

  // Si URL -> récupère "public|protected|private/..."
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const m = u.pathname.match(/\/(public|protected|private)\/(.+)$/);
      if (m) return `${m[1]}/${m[2]}`; // ex: "public/…"
      // fallback: on force public/
      return `public/${u.pathname.replace(/^\/+/, '')}`;
    } catch {
      /* ignore */
    }
  }

  // Si déjà préfixée par public|protected|private -> on garde
  const cleaned = s.replace(/^\/+/, '');
  if (/^(public|protected|private)\//.test(cleaned)) return cleaned;

  // Sinon on force public/
  return `public/${cleaned}`;
}

@Component({
  components: { VueSlider },
})
export default class RaindropAnalyze extends Vue {
  @Prop({ type: Array, required: true }) readonly photos!: PhotoLite[];
  @Prop({ type: String, required: true }) readonly bucket!: string;
  @Prop({ type: String, required: true }) readonly cameraId!: string;
  @Prop({ type: String, required: true }) readonly apiUrl!: string; // ex: VUE_APP_RAINDROP_URL
  @Prop({ type: String, required: true }) readonly jwt!: string;
  @Prop({ type: Function, default: null }) readonly urlResolver!:
    | ((key: string) => Promise<string | null | undefined>)
    | null;

  @Ref('baseImg') readonly baseImgRef!: HTMLImageElement;
  @Ref('overlay') readonly overlayRef!: HTMLCanvasElement;
  @Ref('stage') readonly stageRef!: HTMLElement;
  @Ref('chartRaindrop') readonly chartRef!: HTMLDivElement;
  @Ref('chartRaindropPct') readonly chartPctRef!: HTMLDivElement;
  @Ref('media') readonly mediaRef!: HTMLElement;

  @State('user', { namespace: 'profile' }) private user!: any;

  visible = false;
  plotlyReady = !!(window as any).Plotly;

  lastImageUrl = '';
  imgErrorOnce = false;

  // ROI
  points: Array<{ x: number; y: number }> = [];
  normalizedRoi: Array<{ x: number; y: number }> = [];

  // Analyse
  minMaskPct = 0.02;
  running = false;
  done = 0;
  total = 0;
  rows: Row[] = [];
  annotatedCount = 0;
  annotatedPrefix = 'pred/raindrop/';

  minCount = 0;
  minAreaPct = 0;

  undoEnabled = false;

  minConf = 0.5;

  get countMax(): number {
    if (!this.rows.length) return 10;
    return Math.max(10, ...this.rows.map((r) => Number(r.count ?? r.goutte ?? 0)));
  }

  // --- Filtrage "visuel" (ce que l’on affiche dans les graphes / KPI)
  get filteredRows() {
    return this.rows
      .map((r) => {
        const p = this.projectRowWithThresholds(r);
        return { ...r, count: p.count, area_pct: p.area_pct };
      })
      .filter((r) => r.count >= this.minCount && r.area_pct >= this.minAreaPct);
  }

  // --- Set d’IDs visibles côté parent (sécurité, comme MotionAnalyze)
  get visiblePhotoIdSet(): Set<string> {
    const ids = (this.photos || []).map((p) => String((p as any).id || (p as any).key)).filter(Boolean);
    return new Set(ids);
  }

  // --- IDs à exclure réellement (sous l’un OU l’autre seuil)
  get atOrAboveThresholdIds(): string[] {
    const vis = this.visiblePhotoIdSet;

    // filteredRows applique déjà projectRowWithThresholds (=> minConf, minMaskPct, etc.)
    return this.filteredRows.map((r: any) => String(r.id)).filter((id) => vis.has(id));
  }

  excludeAtOrAboveThreshold(): void {
    const ids = this.atOrAboveThresholdIds;
    if (!ids.length) return;
    this.$emit('exclude-photos', ids);
    this.undoEnabled = true;
  }

  get hasPolygon() {
    return (this.points?.length ?? 0) > 0 || (this.normalizedRoi?.length ?? 0) > 0;
  }
  get canUndo() {
    return (this.points?.length ?? 0) > 0;
  }
  get progressPct() {
    return this.total ? Math.round((100 * this.done) / this.total) : 0;
  }

  get quickKpis() {
    if (!this.rows.length) return [];
    const base = this.filteredRows.length ? this.filteredRows : this.rows;

    const counts = base.map((r) => Number(r.count ?? r.goutte ?? 0));
    const area = base.map((r) => Number(r.area_pct ?? 0));

    const photosWithOne = base.filter((r) => Number(r.count ?? r.goutte ?? 0) > 0).length;

    const avgPct = area.length ? area.reduce((a, b) => a + b, 0) / area.length : 0;
    const minPct = area.length ? Math.min(...area) : 0;
    const maxPct = area.length ? Math.max(...area) : 0;

    const maxCount = counts.length ? Math.max(...counts) : 0;

    return [
      { key: 'count', title: 'Photos analysées', value: base.length },
      { key: 'has1', title: 'Photos avec ≥ 1 goutte', value: photosWithOne },
      { key: 'avgpct', title: '% moyen', value: `${avgPct.toFixed(1)}%` },
      { key: 'maxpct', title: '% le plus haut', value: `${maxPct.toFixed(1)}%` },
      { key: 'minpct', title: '% le plus bas', value: `${minPct.toFixed(1)}%` },
      { key: 'max', title: 'Max nb gouttes', value: `${maxCount}` }, // on conserve le max nb gouttes
    ];
  }

  @Watch('minCount')
  @Watch('minAreaPct')
  onThresholdsChanged() {
    this.renderCharts();
  }

  undoExclude(): void {
    this.$emit('undo-exclude-request');
    this.undoEnabled = false;
  }

  @Watch('visible', { immediate: true })
  async onVisible(v: boolean) {
    this.$emit(v ? 'raindrop-open' : 'raindrop-closed');
    if (!v) return;
    if (!this.plotlyReady) {
      await new Promise<void>((resolve) => {
        const s = document.createElement('script');
        s.src = 'https://cdn.plot.ly/plotly-2.30.0.min.js';
        s.onload = () => resolve();
        document.head.appendChild(s);
      });
      this.plotlyReady = true;
    }
    await this.refreshLastImageUrl();
    this.$nextTick(() => {
      this.syncOverlaySize();
      (window as any).Plotly?.Plots.resize(this.chartRef);
      (window as any).Plotly?.Plots.resize(this.chartPctRef);
      this.renderCharts();
    });
  }

  async refreshLastImageUrl() {
    const last = this.photos?.[this.photos.length - 1];
    if (!last) {
      this.lastImageUrl = '';
      return;
    }

    const rawKey = String((last as any).key || '');

    // 1) Si on te passe déjà une URL *et qu’elle est valide* (contient /public|protected|private/),
    //    on la prend. Sinon on re-signe nous-mêmes.
    if (/^https?:\/\//i.test(rawKey)) {
      try {
        const u = new URL(rawKey);
        if (/\/(public|protected|private)\//.test(u.pathname)) {
          this.lastImageUrl = rawKey;
          return;
        }
      } catch {
        /* ignore */
      }
    }

    // 2) Si on a un urlResolver, on l’essaie MAIS on vérifie le path; sinon on re-signe.
    if (this.urlResolver) {
      try {
        const u = await this.urlResolver(rawKey);
        if (u) {
          try {
            const up = new URL(String(u));
            if (/\/(public|protected|private)\//.test(up.pathname)) {
              this.lastImageUrl = String(u);
              return;
            }
          } catch {
            /* ignore */
          }
        }
      } catch {
        /* ignore */
      }
    }

    // 3) 🔒 fallback fiable : on signe nous-mêmes (et on normalise la clé)
    this.lastImageUrl = await getSignedUrlPublic(rawKey);
  }
  async onImgError() {
    if (this.imgErrorOnce) return;
    this.imgErrorOnce = true;
    try {
      const last = this.photos[this.photos.length - 1];
      this.lastImageUrl = await getSignedUrlPublic(normalizePublicKey(last.key)); // ⬅️
    } catch {
      /* ignore */
    }
  }

  // ===== ROI Drawing (copie légère de MotionAnalyze) =====
  syncOverlaySize(): void {
    const img = this.baseImgRef,
      c = this.overlayRef,
      media = this.mediaRef;
    if (!img || !c || !media) return;
    const iw = Math.round(img.clientWidth),
      ih = Math.round(img.clientHeight);
    if (!iw || !ih) {
      requestAnimationFrame(() => this.syncOverlaySize());
      return;
    }
    c.width = iw;
    c.height = ih;
    c.style.width = iw + 'px';
    c.style.height = ih + 'px';
    this.redraw();
  }
  toNormalized(pt: { x: number; y: number }) {
    const c = this.overlayRef;
    return {
      x: Math.max(0, Math.min(1, pt.x / (c?.width || 1))),
      y: Math.max(0, Math.min(1, pt.y / (c?.height || 1))),
    };
  }
  toCanvas(pt: { x: number; y: number }) {
    const c = this.overlayRef;
    return { x: pt.x * (c?.width || 1), y: pt.y * (c?.height || 1) };
  }
  onCanvasDown(e: MouseEvent) {
    const c = this.overlayRef;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const x = e.clientX - rect.left,
      y = e.clientY - rect.top;
    this.points.push({ x, y });
    this.normalizedRoi = this.points.map((p) => this.toNormalized(p));
    this.redraw();
  }
  closePolygon() {
    if ((this.points || []).length >= 3) {
      this.normalizedRoi = this.points.map((p) => this.toNormalized(p));
      this.redraw(true);
    }
  }
  undoPoint() {
    if ((this.points || []).length) {
      this.points.pop();
      this.normalizedRoi = this.points.map((p) => this.toNormalized(p));
      this.redraw();
    }
  }
  clearPolygon() {
    this.points = [];
    this.normalizedRoi = [];
    this.redraw();
  }
  redraw(closed = false) {
    const c = this.overlayRef;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    const pts = this.points.length ? this.points : this.normalizedRoi.map((p) => this.toCanvas(p));
    if (!pts.length) return;

    if (closed || this.normalizedRoi.length >= 3) {
      const poly = this.normalizedRoi.length ? this.normalizedRoi.map((p) => this.toCanvas(p)) : pts;
      ctx.save();
      ctx.beginPath();
      poly.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.closePath();
      ctx.fillStyle = 'rgba(255, 225, 0, 0.25)';
      ctx.fill();
      ctx.restore();
    }
    ctx.beginPath();
    pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
    if (closed || pts.length >= 3) ctx.closePath();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#00aa55';
    ctx.stroke();
    ctx.fillStyle = '#007a3d';
    pts.forEach((p) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  // ===== Analyse
  async startAnalysis() {
    if (!this.photos.length) {
      alert('Aucune photo.');
      return;
    }

    this.running = true;
    this.done = 0;
    this.total = this.photos.length;
    this.annotatedCount = 0;

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.jwt) headers.Authorization = `Bearer ${this.jwt}`;

    const jobs = this.photos.map((p) => async () => {
      const body = {
        bucket: this.bucket,
        key: keyForLambda(p.key), // <-- la clé correcte pour S3/Lambda
        photoId: p.id || p.key,
        cameraId: this.cameraId,
        // pas de ROI => on NE PASSE PAS polygonPoints
        // ROI (si présent) : points normalisés [0..1]
        ...(this.normalizedRoi.length >= 3 && {
          polygonPoints: this.normalizedRoi.map((pt) => ({
            x: +(+pt.x).toFixed(5),
            y: +(+pt.y).toFixed(5),
          })),
        }),
        conf: 0.25,
        iou: 0.5,
        min_mask_pct: this.minMaskPct,
      };
      //console.log('[RAINDROP] call body =', body);
      const res = await fetch(this.apiUrl, { method: 'POST', headers, body: JSON.stringify(body) });
      const raw = await res.json().catch(() => ({} as any));
      const payload = typeof raw?.body === 'string' ? JSON.parse(raw.body) : raw.body || raw;

      // La lambda renvoie { results: [ { key, goutte_count, goutte_pct } ], ddb:{...} }
      const item = Array.isArray(payload?.results)
        ? payload.results.find((r: any) => String(r.key) === String(p.key)) || payload.results[0]
        : payload;

      const gouttePct = Number(item?.goutte_pct ?? item?.area_pct ?? 0); // % surface (si tu veux garder)
      const goutteCount = Number(item?.goutte_count ?? item?.raindrop_count ?? 0); // nb de gouttes

      const areaPct100 = Number(item?.goutte_pct ?? item?.area_pct ?? 0); // déjà 0–100

      const r: Row = {
        id: String(p.id || p.key),
        key: p.key,
        t: new Date(p.time as any).getTime(),
        goutte: Number(item?.goutte_count ?? item?.raindrop_count ?? 0),
        area_pct: Number(item?.goutte_pct ?? item?.area_pct ?? 0),
        count: Number(item?.goutte_count ?? item?.raindrop_count ?? 0),
        detections: Array.isArray(item?.detections)
          ? item.detections.map((d: any) => ({
              conf: Number(d.conf ?? 0),
              area_pct: Number(d.area_pct ?? 0),
            }))
          : undefined,
        annotatedKey: item?.annotatedKey,
      };

      if (r.annotatedKey) this.annotatedCount++;
      this.mergeRows([r]);
      this.done++;
      this.renderCharts();
      return r;
    });

    // Concurrency au besoin (24 si ça te va)
    await this.runPool(jobs, 100).catch(console.error);
    this.running = false;
    this.renderCharts();
  }

  private projectRowWithThresholds(r: Row) {
    // Si on n’a pas le détail, on retombe sur l’agrégé existant
    if (!r.detections?.length) {
      const ok = Number(r.count ?? r.goutte ?? 0) >= this.minCount && Number(r.area_pct ?? 0) >= this.minAreaPct;
      return ok
        ? { count: Number(r.count ?? r.goutte ?? 0), area_pct: Number(r.area_pct ?? 0) }
        : { count: 0, area_pct: 0 };
    }

    // Filtre par confiance ET % surface masque local
    const kept = r.detections.filter((d) => d.conf >= this.minConf && d.area_pct >= this.minMaskPct * 100);

    // Agrégation post-filtrage (compte + somme des % surfaces)
    const count = kept.length;
    const area_pct = kept.reduce((s, d) => s + d.area_pct, 0);

    // Applique ensuite tes seuils globaux d’affichage
    const pass = count >= this.minCount && area_pct >= this.minAreaPct;
    return pass ? { count, area_pct } : { count: 0, area_pct: 0 };
  }

  mergeRows(newRows: Row[]) {
    const map = new Map(this.rows.map((r) => [r.id, r]));
    for (const r of newRows) {
      map.set(r.id, { ...(map.get(r.id) || {}), ...r });
    }
    this.rows = Array.from(map.values()).sort((a, b) => a.t - b.t);
  }

  async runPool<T>(jobs: Array<() => Promise<T>>, limit: number) {
    const ret: T[] = [];
    let i = 0;
    const workers = new Array(Math.min(limit, jobs.length)).fill(0).map(async () => {
      while (i < jobs.length) {
        const cur = i++;
        const r = await jobs[cur]().catch((e) => ({} as any));
        ret[cur] = r;
        await Promise.resolve();
      }
    });
    await Promise.all(workers);
    return ret;
  }

  @Watch('minConf')
  onMinConfChanged() {
    this.renderCharts();
  }

  renderCharts() {
    if (!this.plotlyReady || !this.visible) return;

    const elCount = this.chartRef;
    const elPct = this.chartPctRef;
    if (!elCount || !elPct) return;

    const rows = this.filteredRows.length ? this.filteredRows : this.rows;
    if (!rows.length) {
      (window as any).Plotly?.purge(elCount);
      (window as any).Plotly?.purge(elPct);
      return;
    }

    const xt = rows.map((r) => new Date(r.t));
    const yCount = rows.map((r) => Number(r.count ?? r.goutte ?? 0));
    const yPct = rows.map((r) => Number(r.area_pct ?? 0)); // en %

    const common = {
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      font: { color: '#5fc9e4', family: 'Poppins, sans-serif', size: 12 },
      xaxis: { type: 'date', color: '#5fc9e4', gridcolor: '#1c304a' },
      margin: { l: 60, r: 20, t: 25, b: 40 },
    } as const;

    // 1) Nb gouttes
    (window as any).Plotly?.react(
      elCount,
      [
        {
          x: xt,
          y: yCount,
          mode: 'lines+markers',
          name: 'Nb gouttes',
          line: { width: 2 },
          marker: { size: 6 },
          hovertemplate: '%{x}<br>gouttes: %{y}<extra></extra>',
        },
      ],
      {
        ...common,
        yaxis: { color: '#5fc9e4', gridcolor: '#1c304a', rangemode: 'tozero', title: 'Nb gouttes' },
      },
      { responsive: true, displaylogo: false }
    );

    // 2) % surface
    (window as any).Plotly?.react(
      elPct,
      [
        {
          x: xt,
          y: yPct,
          mode: 'lines+markers',
          name: '% surface',
          line: { width: 2 },
          marker: { size: 6 },
          hovertemplate: '%{x}<br>% surface: %{y:.2f}%<extra></extra>',
        },
      ],
      {
        ...common,
        yaxis: { color: '#5fc9e4', gridcolor: '#1c304a', rangemode: 'tozero', title: '% surface' },
      },
      { responsive: true, displaylogo: false }
    );
  }
}
